import { useMemo } from "react";

import {
  useFeatureFlag,
  useGetFieldsByFarmId,
  useGetSoilSightCoverCropTiming,
  useGetSoilSightCropDatabase,
  useGetSoilSightFieldSoil,
  useGetSoilSightNonCropDatabase,
  useGetSoilSightProject,
  useGetSoilSightScenarioDetails,
  useListSoilSightProjects,
  useListSoilSightScenarios,
} from "utils";

import { FEATURE_FLAGS, SHOW_SOIL_SIGHT_FIELD_SUMMARY } from "consts";

import { useMainCropLabelLookup } from "pages/app/scenario-analysis/hooks/useMainCropOptions";

type SoilSightPageDataArgs = {
  klimFarmId: string;
  soilSightProjectId: string | null;
  /** Per-project queries stay off until the mount cache reset has finished, so it cannot race a fetch. */
  soilSightCacheReady: boolean;
};

/** Every server read the analysis page needs, plus the values derived straight from them. */
export const useSoilSightPageData = ({ klimFarmId, soilSightProjectId, soilSightCacheReady }: SoilSightPageDataArgs) => {
  const queriesProjectId = soilSightCacheReady ? soilSightProjectId ?? "" : "";

  const projectQuery = useGetSoilSightProject(queriesProjectId);
  const scenarioListQuery = useListSoilSightScenarios(queriesProjectId);
  const listProjectsQuery = useListSoilSightProjects(klimFarmId);
  const projects = useMemo(() => listProjectsQuery.data ?? [], [listProjectsQuery.data]);
  const {
    data: rawFarmFields,
    isFetching: farmFieldsFetching,
    refetch: refetchFarmFields,
  } = useGetFieldsByFarmId({ farmId: klimFarmId, enabled: !!klimFarmId });
  const farmFields = useMemo(() => rawFarmFields ?? [], [rawFarmFields]);

  // Fetched once for every drawer; non-blocking, so drawers render without defaults while these load.
  const { data: cropDefaultsByCropId } = useGetSoilSightCropDatabase();
  const { data: nonCropDefaults } = useGetSoilSightNonCropDatabase();
  // Crop names for the Detailed calculations tab; shares OutcomeComparison's cached query.
  const { mainCropLabelLookup } = useMainCropLabelLookup();

  // The list endpoint returns summaries only, so fan out per-scenario detail to hydrate crop years.
  const scenarioIds = useMemo(() => scenarioListQuery.data?.map((scenario) => scenario.id) ?? [], [scenarioListQuery.data]);
  const {
    data: scenarioDetails,
    loading: anyDetailLoading,
    error: anyDetailError,
    firstError: firstDetailError,
  } = useGetSoilSightScenarioDetails(scenarioIds, {
    enabled: soilSightCacheReady && !!soilSightProjectId,
  });
  const allDetailsResolved = scenarioIds.length === 0 || (!anyDetailLoading && !anyDetailError && scenarioDetails.length === scenarioIds.length);

  const projectFieldId = projectQuery.data?.settingsRefinements?.fieldId ?? null;

  // The field's soil readings need no forecast, so they load with the field rather than with a run; only fetched while their section is shown.
  const { data: fieldSoil } = useGetSoilSightFieldSoil(
    SHOW_SOIL_SIGHT_FIELD_SUMMARY ? soilSightProjectId : null,
    projectFieldId,
    projectQuery.data?.settingsRefinements?.silverServiceVersion ?? null
  );

  // Silver's cover-crop timing for the field's region, so the builder warns about a cover the model would skip.
  const { isEnabled: isCoverCropFitWarningEnabled } = useFeatureFlag(FEATURE_FLAGS.SOIL_SIGHT_COVER_CROP_FIT_WARNING, { project: "common" });
  const { data: fetchedCoverCropTiming } = useGetSoilSightCoverCropTiming(
    soilSightProjectId,
    projectFieldId,
    projectQuery.data?.settingsRefinements?.silverServiceVersion ?? null,
    isCoverCropFitWarningEnabled
  );
  // Dropped while the flag is off, so timing cached before it was switched off shows no warnings.
  const coverCropTiming = isCoverCropFitWarningEnabled ? fetchedCoverCropTiming : undefined;

  const projectHasScenarios = (scenarioListQuery.data?.length ?? 0) > 0;
  // Mid-refetch the 24h-cached list can still contain the just-deleted field, which would hide the prompt.
  const fieldsForProjectFarmLoaded = !!klimFarmId && projectQuery.data?.farmId === klimFarmId && rawFarmFields !== undefined && !farmFieldsFetching;

  return {
    projectQuery,
    scenarioListQuery,
    listProjectsQuery,
    projects,
    farmFields,
    farmFieldsFetching,
    refetchFarmFields,
    cropDefaultsByCropId,
    nonCropDefaults,
    mainCropLabelLookup,
    scenarioDetails,
    anyDetailLoading,
    anyDetailError,
    firstDetailError,
    allDetailsResolved,
    projectFieldId,
    fieldSoil,
    coverCropTiming,
    projectHasScenarios,
    fieldsForProjectFarmLoaded,
  };
};
